import {
  CREATOR_STATUS,
  SAFE_COLLAB_STATUS,
  type CampaignCreatorApiResponse,
  type CampaignCreatorRow,
  type CreatorStatus,
  type SafeCollabStatus,
} from '../types/creatorTabTypes';

function deriveSafeCollabStatus(
  vettingDecision: string | null
): SafeCollabStatus {
  if (vettingDecision === 'approved') {
    return SAFE_COLLAB_STATUS.Vetted;
  }
  if (vettingDecision === 'declined') {
    return SAFE_COLLAB_STATUS.Warning;
  }
  return SAFE_COLLAB_STATUS.NotVetted;
}

// These sets mirror CampaignV2::CreatorSerializer::STATES_BY_STATUS in
// PPSuite — the contract for which aasm states each status filter returns.
// Keep them in sync so derivation is total over every row the endpoint can
// send back.
const HIRED_STATES = new Set([
  'awaiting_influencer_uploads',
  'awaiting_brand_uploads_feedback',
  'awaiting_influencer_posts',
  'awaiting_payment',
  'completed',
]);
const DECLINED_STATES = new Set(['rejected', 'expired']);

function deriveApplicantStatus(aasmState: string): CreatorStatus {
  if (HIRED_STATES.has(aasmState)) {
    return CREATOR_STATUS.Hired;
  }
  if (DECLINED_STATES.has(aasmState)) {
    return CREATOR_STATUS.Declined;
  }
  return CREATOR_STATUS.Applications;
}

export default function mapApiResponseToCreatorRow(
  raw: CampaignCreatorApiResponse
): CampaignCreatorRow {
  return {
    id: raw.id,
    profileId: raw.profileId,
    creatorName: raw.creatorName,
    avatarUrl: raw.avatarUrl,
    age: raw.age ?? null,
    location: raw.location ?? null,
    portfolioImages: raw.portfolioImages ?? [],
    followers: raw.cachedFollowerCount ?? 0,
    engagementRate: raw.averageEngagementRate ?? 0,
    lists: raw.membershipLabels.map((l) => ({name: l.name})),
    safeCollabStatus: deriveSafeCollabStatus(raw.brandSafetyVettingDecision),
    campaignHistoryCount: raw.completedPaidGigsCount,
    campaignHistoryRating: raw.rating,
    appliedDate: raw.appliedAt,
    aasmState: raw.aasmState,
    // Always derived from the row's own state — never stamped with the
    // active filter. Rows shown via keepPreviousData right after a filter
    // switch keep their true status, so status-dependent actions (grid CTA,
    // row menu) stay correct on them.
    applicantStatus: deriveApplicantStatus(raw.aasmState),
    paidGigId: raw.paidGigId ?? null,
    vettingId: raw.vettingId ?? null,
    merchantConversationId: raw.merchantConversationId ?? null,
    merchantUnreadMessagesCount: raw.merchantUnreadMessagesCount ?? 0,
    merchantCampaignUnreadMessagesCount:
      raw.merchantCampaignUnreadMessagesCount ?? 0,
    brandSafetyVettingDecision: raw.brandSafetyVettingDecision,
    costInCents: raw.costInCents ?? null,
    brandNegotiationSnapshot: raw.brandNegotiationSnapshot ?? null,
    audienceTopCountry: raw.audienceTopCountry ?? null,
    averageViews: raw.averageViews ?? null,
    contentDue: raw.contentDue ?? null,
    acceptedAssetsCount: raw.acceptedAssetsCount ?? null,
    requiredAssetsCount: raw.requiredAssetsCount ?? null,
    pendingAssetsCount: raw.pendingAssetsCount ?? null,
    editsRequestedAssetsCount: raw.editsRequestedAssetsCount ?? null,
    notSubmittedCount: raw.notSubmittedCount ?? null,
    extraAssetsCount: raw.extraAssetsCount ?? null,
    requirementsMet: raw.requirementsMet ?? false,
    gigInTerminalState: raw.gigInTerminalState ?? false,
    postedState: raw.postedState ?? null,
    isOverdue: raw.isOverdue ?? false,
    dueSoon: raw.dueSoon ?? false,
    maxRevisionRound: raw.maxRevisionRound ?? 0,
    deliverables: raw.deliverables ?? [],
    shippingAddressFormatted: raw.shippingAddressFormatted ?? null,
    shippingStatus: raw.shippingStatus ?? null,
    brandNote: raw.brandNote ?? null,
    isShortlisted: raw.isShortlisted ?? false,
    deliverableId: raw.deliverableId ?? null,
    fulfillmentState: raw.fulfillmentState ?? null,
    deliveredAt: raw.deliveredAt ?? null,
    trackingCode: raw.trackingCode ?? null,
    trackingCodeUrl: raw.trackingCodeUrl ?? null,
    trackingCodeInvalidatedAt: raw.trackingCodeInvalidatedAt ?? null,
    trackingCodeInvalidatedReason: raw.trackingCodeInvalidatedReason ?? null,
    couponCode: raw.couponCode ?? null,
    carrier: raw.carrier ?? null,
    deliveryStatus: raw.deliveryStatus ?? null,
    expectedDeliveryDate: raw.expectedDeliveryDate ?? null,
    lastCheckpointCity: raw.lastCheckpointCity ?? null,
    lastCheckpointState: raw.lastCheckpointState ?? null,
    lastCheckpointZip: raw.lastCheckpointZip ?? null,
    lastCheckpointMessage: raw.lastCheckpointMessage ?? null,
    lastCheckpointDate: raw.lastCheckpointDate ?? null,
    totalCheckpointsCount: raw.totalCheckpointsCount ?? null,
  };
}
